import { Calendar, Ellipsis, Plus } from "lucide-react";
import { useUpdateKanban } from "./useUpdateKanban";
import { useNavigate } from "react-router-dom";
import { KanbanBoard, KanbanCard } from "@/ui/KanbanBoard";
import { formatDate, toPascalCase } from "@/utils/helpers";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { v4 as uuidv4 } from "uuid";

import { Input } from "@/components/ui/input";
import { z } from "zod";
import { useCreatePursuit } from "./useCreatePursuit";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

import PursuitStatusSelect from "./PursuitStatusSelect";
import DatePicker from "@/components/ui/date-picker";
import { Separator } from "@/components/ui/separator";
import TextEditor from "@/ui/TextEditor";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import PursuitOperations from "./PursuitOperations";
export const statusColumns = [
  { id: "assessment", title: "Assessment" },
  { id: "in_progress", title: "In Progress" },
  { id: "submitted", title: "Submitted" },
  {
    id: "won",
    title: "Won",
    columnColor: "var(--kanban-column-green)",
    cardColor: "var(--kanban-card-green)",
    cardBorderColor: "hsl(var(--border))",
    addCardColor: "var(--emerald-300)",
    operationColor: "var(--emerald-800)",
    operationBorderColor: "var(--emerald-400)",
  },
];

const formSchema = z.object({
  title: z.string().trim().min(1, {
    message: "Title cannot be empty",
  }),
  description: z.any().optional(),
  award_reason: z.any().optional(),
  status: z.string().trim(),
  due_date: z.date().optional(),
});

export function PursuitCreator({ status, ...props }) {
  const [proposalId, setProposalId] = useState(uuidv4());
  const { mutate, isLoading } = useCreatePursuit("upload", proposalId);
  const [open, onOpenChange] = useState(false);
  const queryClient = useQueryClient();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      status: status || "assessment",
    },
  });
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(["user-pursuits"]);
    };
  }, [queryClient]);
  function onSubmit(data) {
    mutate({
      ...data,
      proposal_id: proposalId,
      search_type: "upload",
    });
    onOpenChange(false);
  }

  return (
    <div>
      <Button onClick={() => onOpenChange(true)} {...props}>
        <Plus /> New Journey
      </Button>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <div className="hidden">
          <SheetTitle />
        </div>
        <SheetContent side="right" style={{ maxWidth: "60vw" }}>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col space-y-4 h-full"
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Journey Title"
                        className="md:text-2xl font-bold border-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:font-normal placeholder:text-muted-foreground/50"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="grid grid-cols-[12rem_1fr] items-center">
                      <FormLabel className="text-muted-foreground">
                        Status
                      </FormLabel>
                      <PursuitStatusSelect
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem className="grid grid-cols-[12rem_1fr] items-center">
                      <FormLabel className="text-muted-foreground">
                        Due Date
                      </FormLabel>
                      <div>
                        <DatePicker
                          selected={field.value}
                          onSelect={field.onChange}
                          placeholder="Pick due date"
                          className="border-none bg-transparent w-full"
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Separator className="bg-muted/50" />
              <FormField
                control={form.control}
                name="award_reason"
                render={({ field }) => (
                  <FormItem className="h-[20rem] flex flex-col flex-grow overflow-y-auto">
                    <FormLabel className="text-muted-foreground">
                      Deep Insights
                    </FormLabel>
                    <FormControl className="flex-grow flex flex-col">
                      <TextEditor
                        className="overflow-y-auto flex-grow"
                        onChange={(editor) =>
                          field.onChange({ blocks: editor.document })
                        }
                        editable={!isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Separator className="bg-muted/50" />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="h-full flex flex-col flex-grow overflow-y-auto">
                    <FormLabel className="text-muted-foreground">
                      Description
                    </FormLabel>
                    <FormControl className="flex-grow flex flex-col">
                      <TextEditor
                        className="overflow-y-auto flex-grow"
                        onChange={(editor) =>
                          field.onChange({ blocks: editor.document })
                        }
                        editable={!isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="mt-4">
                Create Journey
              </Button>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function PursuitKanban({ data, isLoading, className }) {
  const { mutate } = useUpdateKanban();
  const navigate = useNavigate();

  return (
    <KanbanBoard
      statusColumns={statusColumns}
      updateKanban={mutate}
      isLoading={isLoading}
      renderCreateCard={(props) => (
        <PursuitCreator
          className="h-12 w-full border font-normal  bg-transparent text-muted-foreground hover:text-muted-foreground"
          variant="muted"
          {...props}
        />
      )}
    >
      {data?.map((pursuit) => (
        <KanbanCard
          item={{ ...pursuit, draggableId: pursuit.id }}
          key={pursuit.id}
          onClick={() => navigate(`./${pursuit.id}/overview`)}
          className="group hover:shadow-lg"
        >
          <div className="relative font-medium mb-2 hover:underline cursor-pointer">
            {pursuit.title}
            <PursuitOperations
              pursuit={pursuit}
              renderDropDownMenuTrigger={() => (
                <DropdownMenuTrigger
                  className="px-2 h-8 absolute top-0 right-0 group-hover:border group-hover:bg-[var(--gray-100)] hover:!bg-[var(--gray-50)] rounded-md duration-0"
                  onClick={(e) => e.stopPropagation()} // Prevents onClick from bubbling
                >
                  <Ellipsis className="hidden group-hover:block w-4 h-4" />
                </DropdownMenuTrigger>
              )}
            />
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className={"rounded-md"}>
                {["sled", "sbir"].includes(pursuit.search_type)
                  ? pursuit.search_type.toUpperCase()
                  : toPascalCase(pursuit.search_type)}
              </Badge>
            </div>
            {pursuit?.due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(pursuit?.due_date)}</span>
              </div>
            )}
          </div>
        </KanbanCard>
      ))}
    </KanbanBoard>
  );
}
